import React, { useEffect } from "react";
import "./App.css";
import "@vibe/core/tokens";
import { Routes, Route } from "react-router-dom";
import { ConfigProvider } from "antd";
import { ThemeProvider } from "@vibe/core";
import { initMixpanel, identifyMixpanelUser } from "@utils/mixpanel/mixpanelService";
import { crispService } from "@services/crisp/crispService";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import "./index.css";

// Redux
import { AppDispatch } from "./redux/store/store";
import { fetchMondayContext } from "./redux/slices/mondaySlice";
import { fetchAccessToken } from "./redux/slices/accessTokenSlice";

// Hooks
import useUser from "./hooks/context/userHook";
import useTokenHook from "./hooks/context/useTokenHook";
import { useCrispChat, usePageLoadAnalytics } from "./hooks/utils";
import useNudgeTrigger from "./hooks/api/useNudgeTrigger";

// Pages
import Home from "./pages/home";
import Onboarding from "./pages/onboarding";
import Screen1 from "./pages/onboarding/screen1";
import AuthorizePage from "./pages/onboarding/AuthorizePage";
import WelcomePage from "./pages/onboarding/welcomePage";
import MondayAuthPage from "./pages/onboarding/MondayAuthPage";
import CancelAuthPage from "./pages/onboarding/CancelAuthPage";
import BatchItemMenu from "./pages/batchItemMenu";
import BatchUnlinkMenu from "./pages/BatchUnlinkMenu";
import BoardItemMenu from "./pages/BoardItemMenu";
import Resynchronization from "./pages/Resynchronization";
import DetailedUsageReport from "./pages/DetailedUsageReport";
import AuthorizedUsersPage from "./pages/AuthorizedUsersPage";
import SettingsPage from "./pages/SettingsPage";
import AddColumn from "./pages/AddColumn";
import BoardsDialog from "./pages/BoardsDialog";

// Components
import { ConsentGate } from "@components/LegalConsent";
import { FreeMonthOffer, FreeMonthOfferPage } from "@components/FreeMonthOffer";
import PageLoader from "./components/PageLoader";
import DeleteModal from "./components/DeleteModalUI/DeleteModal";
import RemoveModal from "./components/DeleteModalUI/RemoveModal";
import UnlinkModal from "./components/DeleteModalUI/UnlinkModal";
import { SyncWizardModal } from "@components/Resynchronization/EmptyState/SyncWizard.tsx/SyncWizardModal";

// Lazy so the fixtures never land in the main production chunk.
const DiscrepancyPreview = React.lazy(() => import("./pages/DiscrepancyPreview"));

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
    CRISP_TOKEN_ID: string;
  }
}

// Initialize Crisp chat
crispService.configure(process.env.REACT_APP_WEB_ID || "");
// Initialize Mixpanel session replay
initMixpanel();

export default function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();
  const pathname = useLocation().pathname;
  // Custom hooks for side effects
  useCrispChat(userId, accountId);
  usePageLoadAnalytics(userId, accountId);
  useNudgeTrigger();

  // Initialize Monday context on mount
  useEffect(() => {
    dispatch(fetchMondayContext());
  }, [dispatch]);

  // Initialize session token when user is available
  useEffect(() => {
    if (userId && !token) {
      dispatch(fetchAccessToken());
    }
  }, [dispatch, userId, token]);

  // Identify user in Mixpanel for session replay
  useEffect(() => {
    if (userId && accountId) {
      identifyMixpanelUser(String(accountId), String(userId));
    }
  }, [userId, accountId]);

  // Paths that must render without Monday context: standalone views used for
  // visual review outside the Monday iframe, where there is no userId to wait for.
  const CONTEXT_FREE_PATHS = ["/empty-state-board-view", "/discrepancy-preview"];

  if (!userId && !CONTEXT_FREE_PATHS.includes(pathname)) {
    return <PageLoader />;
  }

  return (
    <>
      <ConfigProvider theme={{ token: { fontFamily: "Figtree, sans-serif" } }}>
        {/*
          Pinned to "light", NOT wired to Monday's context theme. Vibe's ThemeProvider is not
          subtree-scoped: it calls document.body.classList.add(...) (see
          @vibe/core/dist/src/components/ThemeProvider/ThemeProviderUtils.js), so following
          Monday's dark/black theme here would flip every Vibe token in the document, app-wide.
          Only the Resynchronization view has been migrated onto those tokens — dozens of other
          files still hardcode light-only Tailwind (bg-white, text-gray-900, bg-gray-50, ...),
          which would render unreadable (e.g. near-black text on dark navy, white on white) the
          moment a Monday user's theme is anything but light. Unpin this only as part of an
          app-wide token migration that removes those hardcoded light-only classes — see
          App.test.tsx's "stays pinned to light" test, which must be updated deliberately, not
          incidentally, when that migration lands.
        */}
        <ThemeProvider systemTheme="light">
          <>
            <ConsentGate />
            <FreeMonthOffer />
            <Routes>
              <Route path="/">
                <Route index element={<Home />} />
                <Route path="onboarding">
                  <Route index element={<Onboarding />} />
                  <Route path="screen1" element={<Screen1 />} />
                  <Route path="screen3" element={<AuthorizePage />} />
                  <Route path="mondayOAuth" element={<MondayAuthPage />} />
                  <Route path="welcome_page" index element={<WelcomePage />} />
                  <Route path="cancelled_authorization" index element={<CancelAuthPage />} />
                </Route>
                <Route index path="settings" element={<SettingsPage />} />
                <Route index path="board-item-menu" element={<BoardItemMenu />} />
                <Route index path="boards-dialog" element={<BoardsDialog />} />
                <Route index path="batch-items-menu" element={<BatchItemMenu />} />
                <Route index path="batch-unlink-items" element={<BatchUnlinkMenu />} />
                <Route index path="resynchronization" element={<Resynchronization />} />
                <Route index path="detailed-usage" element={<DetailedUsageReport />} />
                <Route index path="authorized-users" element={<AuthorizedUsersPage />} />
              </Route>
              <Route index path="add-column" element={<AddColumn />} />
              <Route index path="delete-modal" element={<DeleteModal />} />
              <Route index path="remove-modal" element={<RemoveModal />} />
              <Route index path="unlink-modal" element={<UnlinkModal />} />

              <Route index path="sync-wizard" element={<SyncWizardModal />} />
              <Route index path="free-month-offer" element={<FreeMonthOfferPage />} />
              <Route
                index
                path="discrepancy-preview"
                element={
                  <React.Suspense fallback={<PageLoader />}>
                    <DiscrepancyPreview />
                  </React.Suspense>
                }
              />
            </Routes>
          </>
        </ThemeProvider>
      </ConfigProvider>
    </>
  );
}
